import meta from "./meta.json";
import { splitPostCategories } from "../blog/categories";
import { getPostThumbnail } from "../blog/media";

export const BASE_URL = "https://egodziny.pl";
export const SITE_NAME = "eGodziny";
export const SITE_LOCALE = "pl_PL";
export const DEFAULT_OG_IMAGE = `${BASE_URL}/icon.png`;

const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=pl.egodziny.egodziny_mobile_app&pcampaignid=web_share";
const APP_STORE_URL = "https://apps.apple.com/pl/app/egodziny/id6738045022?l=pl";
const LINKEDIN_URL = "https://www.linkedin.com/company/egodziny/";
const DEFAULT_REVIEW_RATING = 5;
const ORGANIZATION_ID = `${BASE_URL}/#organization`;
const WEBSITE_ID = `${BASE_URL}/#website`;
const LOCAL_BUSINESS_ID = `${BASE_URL}/#local-business`;

const ROUTE_LABELS = {
  "/": "Strona główna",
  "/blog": "Blog",
  "/case-study": "Case studies",
  "/case-study/staldach-bud": "Case study Staldach-bud",
  "/case-study/hr-centre": "Case study HR Centre",
  "/cennik": "Cennik",
  "/praca-tymczasowa-i-outsourcing": "Praca tymczasowa i outsourcing",
  "/inzynieria-i-budownictwo": "Inżynieria i budownictwo",
  "/o-nas": "O nas",
  "/kontakt": "Kontakt",
  "/funkcje": "Funkcje",
  "/app": "Aplikacja",
  "/privacy": "Polityka prywatności",
  "/regulamin": "Regulamin",
};

const stripHtml = (text = "") =>
  text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

export const normalizePath = (path = "/") => {
  if (!path) {
    return "/";
  }

  const [pathWithoutQuery] = path.split(/[?#]/);
  const withLeadingSlash = pathWithoutQuery.startsWith("/")
    ? pathWithoutQuery
    : `/${pathWithoutQuery}`;

  if (withLeadingSlash !== "/" && withLeadingSlash.endsWith("/")) {
    return withLeadingSlash.slice(0, -1);
  }

  return withLeadingSlash || "/";
};

export const toAbsoluteUrl = (path = "/") => {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  return `${BASE_URL}${normalizePath(path)}`;
};

const getFaqMainEntity = (content) => {
  const questions = content?.faq?.questions;
  if (!Array.isArray(questions)) {
    return [];
  }

  return questions
    .filter((item) => item?.question && item?.answer)
    .map((item) => ({
      "@type": "Question",
      name: item.question.trim(),
      acceptedAnswer: {
        "@type": "Answer",
        text: stripHtml(item.answer),
      },
    }));
};

const getReviewItems = (content) => {
  const reviewColumns = [content?.reviews?.col1, content?.reviews?.col2];

  return reviewColumns
    .filter((review) => review?.name && review?.text)
    .map((review, index) => ({
      "@type": "Review",
      name: `Opinia klienta ${index + 1}`,
      reviewBody: stripHtml(review.text),
      reviewRating: {
        "@type": "Rating",
        ratingValue: DEFAULT_REVIEW_RATING,
        bestRating: 5,
        worstRating: 1,
      },
      author: {
        "@type": "Person",
        name: review.name,
      },
    }));
};

const toIsoDate = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value?.toDate === "function") {
    return value.toDate().toISOString();
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.toISOString();
};

const getRouteLabel = (path, fallbackName) => {
  const normalizedPath = normalizePath(path);
  if (fallbackName) {
    return fallbackName;
  }

  if (ROUTE_LABELS[normalizedPath]) {
    return ROUTE_LABELS[normalizedPath];
  }

  if (/^\/blog\/[^/]+$/.test(normalizedPath)) {
    return "Wpis blogowy";
  }

  return normalizedPath
    .split("/")
    .filter(Boolean)
    .slice(-1)[0]
    ?.replace(/-/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase()) || "Podstrona";
};

export const buildOrganizationStructuredData = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORGANIZATION_ID,
  name: SITE_NAME,
  legalName: "ALTO LABS PSA",
  url: BASE_URL,
  logo: DEFAULT_OG_IMAGE,
  image: DEFAULT_OG_IMAGE,
  email: "kontakt@egodziny.pl",
  telephone: "+48 515 800 125",
  sameAs: [LINKEDIN_URL],
  address: {
    "@type": "PostalAddress",
    streetAddress: "ul. Lęborska 3B",
    postalCode: "80-386",
    addressLocality: "Gdańsk",
    addressCountry: "PL",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      email: "kontakt@egodziny.pl",
      telephone: "+48 515 800 125",
      availableLanguage: ["pl", "en"],
      areaServed: "PL",
    },
  ],
});

export const buildLocalBusinessStructuredData = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": LOCAL_BUSINESS_ID,
  name: SITE_NAME,
  url: BASE_URL,
  image: DEFAULT_OG_IMAGE,
  email: "kontakt@egodziny.pl",
  telephone: "+48 515 800 125",
  address: {
    "@type": "PostalAddress",
    streetAddress: "ul. Lęborska 3B",
    postalCode: "80-386",
    addressLocality: "Gdańsk",
    addressCountry: "PL",
  },
  areaServed: {
    "@type": "Country",
    name: "Polska",
  },
  parentOrganization: {
    "@id": ORGANIZATION_ID,
  },
});

export const buildWebSiteStructuredData = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: BASE_URL,
  name: SITE_NAME,
  inLanguage: "pl-PL",
  publisher: {
    "@id": ORGANIZATION_ID,
  },
});

export const buildWebPageStructuredData = ({ path = "/", title, description }) => {
  const normalizedPath = normalizePath(path);
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${toAbsoluteUrl(normalizedPath)}#webpage`,
    url: toAbsoluteUrl(normalizedPath),
    name: title,
    description: description || undefined,
    inLanguage: "pl-PL",
    isPartOf: {
      "@id": WEBSITE_ID,
    },
    about: {
      "@id": ORGANIZATION_ID,
    },
  };
};

export const buildBreadcrumbListStructuredData = (path = "/", currentPageName) => {
  const normalizedPath = normalizePath(path);
  if (normalizedPath === "/") {
    return null;
  }

  const items = [{ name: "Strona główna", path: "/" }];

  if (/^\/blog\/[^/]+$/.test(normalizedPath)) {
    items.push({ name: "Blog", path: "/blog" });
    items.push({
      name: getRouteLabel(normalizedPath, currentPageName),
      path: normalizedPath,
    });
  } else if (/^\/case-study\/[^/]+$/.test(normalizedPath)) {
    items.push({ name: "Case studies", path: "/case-study" });
    items.push({
      name: getRouteLabel(normalizedPath, currentPageName),
      path: normalizedPath,
    });
  } else {
    items.push({
      name: getRouteLabel(normalizedPath, currentPageName),
      path: normalizedPath,
    });
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${toAbsoluteUrl(normalizedPath)}#breadcrumbs`,
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: toAbsoluteUrl(item.path),
    })),
  };
};

export const buildFaqStructuredData = (content, pagePath = "/") => {
  const faqMainEntity = getFaqMainEntity(content);
  if (faqMainEntity.length === 0) {
    return null;
  }

  const normalizedPath = normalizePath(pagePath);

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${BASE_URL}${normalizedPath}#faq`,
    mainEntity: faqMainEntity,
  };
};

export const buildServiceStructuredData = ({
  path = "/",
  name,
  description,
  serviceType,
  audience,
}) => {
  const normalizedPath = normalizePath(path);

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${toAbsoluteUrl(normalizedPath)}#service`,
    serviceType: serviceType || name,
    name,
    description,
    url: toAbsoluteUrl(normalizedPath),
    provider: {
      "@id": ORGANIZATION_ID,
    },
    areaServed: {
      "@type": "Country",
      name: "Polska",
    },
    audience: audience
      ? {
          "@type": "Audience",
          audienceType: audience,
        }
      : undefined,
  };
};

export const buildCollectionPageStructuredData = ({
  path = "/",
  name,
  description,
  itemListElements = [],
}) => {
  const normalizedPath = normalizePath(path);

  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${toAbsoluteUrl(normalizedPath)}#collection-page`,
    url: toAbsoluteUrl(normalizedPath),
    name,
    description,
    isPartOf: {
      "@id": WEBSITE_ID,
    },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: itemListElements.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        url: toAbsoluteUrl(item.url),
      })),
    },
  };
};

export const buildArticleStructuredData = ({
  path = "/",
  headline,
  description,
  authorName = "Zespół eGodziny",
  image = DEFAULT_OG_IMAGE,
  datePublished,
  dateModified,
}) => {
  const normalizedPath = normalizePath(path);
  const published = toIsoDate(datePublished);
  const modified = toIsoDate(dateModified) || published;

  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${toAbsoluteUrl(normalizedPath)}#article`,
    mainEntityOfPage: toAbsoluteUrl(normalizedPath),
    headline,
    description,
    image: toAbsoluteUrl(image),
    datePublished: published || undefined,
    dateModified: modified || undefined,
    author: {
      "@type": "Person",
      name: authorName,
    },
    publisher: {
      "@id": ORGANIZATION_ID,
    },
  };
};

export const buildBlogPostingStructuredData = ({ path = "/", post }) => {
  const normalizedPath = normalizePath(path);
  const contentText = stripHtml(post?.content || "");
  const postCategories = splitPostCategories(post?.category);
  const thumbnail = getPostThumbnail(post);
  const description =
    post?.meta_description ||
    contentText.slice(0, 180) ||
    meta["/blog"]?.description ||
    "";
  const publishedDate = toIsoDate(post?.date);

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${toAbsoluteUrl(normalizedPath)}#blog-post`,
    mainEntityOfPage: toAbsoluteUrl(normalizedPath),
    headline: post?.title || "Wpis blogowy",
    description,
    articleBody: contentText || undefined,
    articleSection:
      postCategories.length > 1
        ? postCategories
        : postCategories[0] || undefined,
    author: {
      "@type": "Person",
      name: post?.author || "Zespół eGodziny",
    },
    publisher: {
      "@id": ORGANIZATION_ID,
    },
    image: toAbsoluteUrl(thumbnail || DEFAULT_OG_IMAGE),
    datePublished: publishedDate || undefined,
    dateModified: publishedDate || undefined,
    url: toAbsoluteUrl(normalizedPath),
    wordCount: contentText ? contentText.split(/\s+/).filter(Boolean).length : undefined,
    keywords: postCategories.length ? postCategories.join(", ") : undefined,
  };
};

export const buildContactPageStructuredData = (path = "/kontakt") => {
  const normalizedPath = normalizePath(path);

  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${toAbsoluteUrl(normalizedPath)}#contact-page`,
    url: toAbsoluteUrl(normalizedPath),
    name: "Kontakt | eGodziny",
    isPartOf: {
      "@id": WEBSITE_ID,
    },
    about: {
      "@id": ORGANIZATION_ID,
    },
  };
};

export const buildAboutPageStructuredData = (path = "/o-nas") => {
  const normalizedPath = normalizePath(path);

  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": `${toAbsoluteUrl(normalizedPath)}#about-page`,
    url: toAbsoluteUrl(normalizedPath),
    name: "O nas | eGodziny",
    isPartOf: {
      "@id": WEBSITE_ID,
    },
    about: {
      "@id": ORGANIZATION_ID,
    },
  };
};

export const buildVideoObjectStructuredData = ({
  path = "/",
  name,
  description,
  contentUrl,
  thumbnailUrl = DEFAULT_OG_IMAGE,
}) => {
  if (!contentUrl) {
    return null;
  }

  const normalizedPath = normalizePath(path);

  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "@id": `${toAbsoluteUrl(normalizedPath)}#video-${name
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "video"}`,
    name,
    description,
    thumbnailUrl: toAbsoluteUrl(thumbnailUrl),
    contentUrl: toAbsoluteUrl(contentUrl),
    embedUrl: toAbsoluteUrl(normalizedPath),
    publisher: {
      "@id": ORGANIZATION_ID,
    },
  };
};

export const buildAppStoreStructuredData = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "@id": `${BASE_URL}/app#software-application`,
  name: SITE_NAME,
  applicationCategory: "BusinessApplication",
  operatingSystem: "iOS, Android",
  url: `${BASE_URL}/app`,
  downloadUrl: GOOGLE_PLAY_URL,
  sameAs: [GOOGLE_PLAY_URL, APP_STORE_URL],
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "PLN",
  },
  publisher: {
    "@id": ORGANIZATION_ID,
  },
});

export const buildHomeStructuredData = (content) => {
  const reviewItems = getReviewItems(content);
  const reviewCount = reviewItems.length;

  const softwareApplication = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": `${BASE_URL}/#software-application`,
    name: "eGodziny",
    url: BASE_URL,
    description:
      meta["/"]?.description ||
      "System eGodziny do ewidencji czasu pracy online.",
    applicationCategory: "BusinessApplication",
    operatingSystem: "iOS, Android, Web",
    isAccessibleForFree: true,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "PLN",
      description: "Darmowy okres testowy 14 dni",
      url: `${BASE_URL}/register`,
    },
    sameAs: [GOOGLE_PLAY_URL, APP_STORE_URL],
    publisher: {
      "@id": ORGANIZATION_ID,
    },
  };

  if (reviewCount > 0) {
    softwareApplication.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: DEFAULT_REVIEW_RATING,
      ratingCount: reviewCount,
      reviewCount,
      bestRating: 5,
      worstRating: 1,
    };
    softwareApplication.review = reviewItems;
  }

  const schemas = [softwareApplication];
  const faqSchema = buildFaqStructuredData(content, "/");
  if (faqSchema) {
    schemas.push(faqSchema);
  }

  return schemas;
};
